import { useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import { Box, IconButton, Menu, MenuItem, Typography, Button, Chip, Input } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import LinkIcon from '@mui/icons-material/Link';
import MovieOutlinedIcon from '@mui/icons-material/MovieOutlined';
import MicTwoTone from '@mui/icons-material/MicTwoTone';
import { useAudioRecorder } from 'react-audio-voice-recorder';
import { useAlert } from 'src/components/alert';
import { MAX_SMS_MEDIA_LABEL } from 'src/constants/misc';

export const SMSGuidelinesWrapper = styled('div')`
  font-size: 12px;
  background-color: #f0f0f0;
  border-radius: 4px;
  padding: 12px;
  overflow-y: auto;
  & p {
    margin: 0;
  }
  & ol {
    margin: 4px 0 0;
    padding: 0 0 0 16px;
  }
`;

export const AudioGuidelinesWrapper = styled('div')`
  font-size: 12px;
  background-color: #f0f0f0;
  border-radius: 4px;
  padding: 12px;
  height: 100%;
  overflow-y: auto;
  & p {
    margin: 0;
  }
  & ul {
    margin: 4px 0 0;
    padding: 0 0 0 16px;
  }
`;

export const SurveyGuidelines = ({ setPricingModalOpen }) => {
  return (
    <SMSGuidelinesWrapper>
      <p>
        Message pricing varies by character count.{' '}
        <button
          onClick={() => setPricingModalOpen(true)}
          type="button"
          style={{
            color: '#0070f3',
            backgroundColor: 'transparent',
            border: 'none',
            padding: 0,
            font: 'inherit',
            cursor: 'pointer',
            textDecoration: 'underline',
          }}
        >
          Click here to view details.
        </button>
      </p>
    </SMSGuidelinesWrapper>
  );
};

/**
 * `dense` drops the grey panel so the hint can sit inside the composer's footer strip, which
 * has neither the width for a panel nor a second background to spare. The wording and the link
 * are the same either way.
 */
const CompressHint = ({ dense, children }) =>
  dense ? (
    <Box sx={{ '& p': { m: 0 } }}>{children}</Box>
  ) : (
    <SMSGuidelinesWrapper>{children}</SMSGuidelinesWrapper>
  );

const CompressLink = ({ url, children }) => (
  <button
    onClick={() => window.open(url, '_blank')}
    type="button"
    style={{
      color: '#0070f3',
      backgroundColor: 'transparent',
      border: 'none',
      padding: 0,
      font: 'inherit',
      cursor: 'pointer',
      textDecoration: 'underline',
    }}
  >
    {children}
  </button>
);

export const CompressImageResource = ({ dense = false }) => {
  return (
    <CompressHint dense={dense}>
      <p>
        This image is too large.{' '}
        <CompressLink url="https://tinypng.com/">Compress image using TinyPNG</CompressLink>
      </p>
    </CompressHint>
  );
};

export const CompressVideoResource = ({ dense = false }) => {
  return (
    <CompressHint dense={dense}>
      <p>
        This video is too large.{' '}
        <CompressLink url="https://www.freeconvert.com/video-compressor">
          Compress video using FreeConvert
        </CompressLink>
      </p>
    </CompressHint>
  );
};

/**
 * `videoFormatsLabel` is only for screens that restrict video formats (the task form); manual
 * batches still take every format in VALID_VIDEO_FILE_TYPES, so they leave it out.
 */
export const SMSGuidelines = ({
  setPricingModalOpen,
  maxMediaLabel = MAX_SMS_MEDIA_LABEL,
  videoFormatsLabel,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <SMSGuidelinesWrapper>
      <p>
        By sending a text you confirm that your organization has been approved for political texting
        and that you are following all federal and state guidelines:
      </p>
      {isOpen ? (
        <div>
          <ol>
            <li>Sending to opted-in recipients</li>
            <li>Add any required disclaimers</li>
            <li>Say who it is from</li>
            <li>Finish with "Text STOP to End"</li>
            <li>Add “Paid for by” with your organization</li>
            <li>Abbreviated URL links are not permitted (example, no Bitly links)</li>
            <li>Only send texts from your approved organization</li>
            <li>Attachments can be a maximum of {maxMediaLabel} in size</li>
            {videoFormatsLabel ? (
              <li>Video attachments must be {videoFormatsLabel} files</li>
            ) : null}
          </ol>
          <button
            onClick={() => setIsOpen(false)}
            type="button"
            style={{
              color: '#0070f3',
              cursor: 'pointer',
              textDecoration: 'underline',
              backgroundColor: 'transparent',
              border: 'none',
              padding: 0,
              font: 'inherit',
              marginTop: 12,
            }}
          >
            Hide examples
          </button>
        </div>
      ) : (
        <button
          onClick={() => setIsOpen(true)}
          type="button"
          style={{
            color: '#0070f3',
            backgroundColor: 'transparent',
            border: 'none',
            padding: 0,
            font: 'inherit',
            cursor: 'pointer',
            textDecoration: 'underline',
            marginTop: 12,
            marginBottom: 12,
          }}
        >
          See examples
        </button>
      )}
      <br />
      <p>
        Refer to Right Insight's{' '}
        <a href="https://rightimpact.org/terms" target="_blank" rel="noreferrer">
          texting policies
        </a>{' '}
        for more information.
      </p>
      <br />
      <p>
        Message pricing varies by character count.{' '}
        <button
          onClick={() => setPricingModalOpen(true)}
          type="button"
          style={{
            color: '#0070f3',
            backgroundColor: 'transparent',
            border: 'none',
            padding: 0,
            font: 'inherit',
            cursor: 'pointer',
            textDecoration: 'underline',
          }}
        >
          Click here to view details.
        </button>
      </p>
    </SMSGuidelinesWrapper>
  );
};

/** `unitPrice` is the org's voice recording price when it has one, otherwise the base price. */
export const AudioGuidelines = ({ unitPrice }) => (
  <AudioGuidelinesWrapper>
    <ul>
      <li>Voice recordings will be sent to voters as a call</li>
      {unitPrice != null ? (
        <li>
          Voicemail pushes are billed at ${unitPrice} per 5 minutes of call time, rounded up for
          each completed call
        </li>
      ) : null}
      <li>Avoid creating voice recording tasks after 7 PM</li>
      <li>Recommend creating the task around noon for all recordings to go through</li>
      <li>
        In your voicemail, you need to state who is calling and any disclaimers required by your
        state, and clarify that you are only calling on behalf of your approved organization within
        the system.
      </li>
    </ul>
  </AudioGuidelinesWrapper>
);

/**
 * What an empty row of the composer's footer strip looks like: a quiet text button in the
 * strip's own caption type, pulled back to its left edge so the label lines up with the rows
 * above and below. Shared, because "add a thing to this message" should not read as two
 * different offers depending on which thing.
 */
const denseRowButtonSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 'fit-content',
  px: 0.5,
  ml: -0.5,
  typography: 'caption',
};

/** The icon that leads either row, at one size in both states. */
const rowIconSx = { fontSize: 16, mr: 0.5 };

/**
 * The clear control for a row of the composer's footer strip. Shared so the attachment and the
 * survey link are removed by the same button in the same place, rather than by two that merely
 * resemble each other.
 */
export const RowRemoveButton = ({ label, onClick }) => (
  <IconButton size="small" aria-label={label} onClick={onClick} sx={{ flexShrink: 0, p: 0.25 }}>
    <CloseIcon sx={{ fontSize: 16 }} />
  </IconButton>
);

/**
 * The attachment as the composer's footer strip shows it: one row, image first. The name is what
 * can grow, so it is the only part allowed to, and it truncates rather than wrapping the row.
 *
 * Size is here because it is the thing that fails: MMS attachments are capped, and a file within
 * the cap can still be worth reconsidering when it is going to every voter in the task.
 */
const AttachmentRow = ({ file, onRemove }) => {
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    if (!file?.type?.startsWith('image/')) {
      setPreviewUrl(null);
      return undefined;
    }
    // Revoked on the way out: without this every re-pick leaks a blob for the page's lifetime.
    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
      {previewUrl ? (
        <Box
          component="img"
          src={previewUrl}
          alt=""
          sx={{
            width: 28,
            height: 28,
            flexShrink: 0,
            objectFit: 'cover',
            borderRadius: 0.5,
            border: '1px solid',
            borderColor: 'divider',
          }}
        />
      ) : (
        <MovieOutlinedIcon sx={{ fontSize: 20, flexShrink: 0, color: 'text.disabled' }} />
      )}
      <Box
        title={file.name}
        sx={{
          flex: 1,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          color: 'text.primary',
        }}
      >
        {file.name}
      </Box>
      <Box component="span" sx={{ flexShrink: 0, color: 'text.disabled' }}>
        {Math.max(1, Math.round(file.size / 1024)).toLocaleString()} KB
      </Box>
      <RowRemoveButton label="Remove attachment" onClick={onRemove} />
    </Box>
  );
};

/**
 * The survey link as a row of the composer's footer strip, deliberately built to the same shape
 * as the attachment above it: an offer to add one while there is none, and once there is, an
 * icon, a name, what it adds and the same clear button.
 *
 * A menu rather than a select, because a select renders a full-width field with a caret at the
 * far end, which reads as a different kind of thing from the button it sits under. Picking the
 * name reopens the menu, so a bank can be swapped without clearing it first.
 */
export const SurveyLinkRow = ({ banks = [], value, onChange, addLabel, meta }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const selected = banks.find((bank) => String(bank?.org_question_bank_id) === String(value ?? ''));

  const choose = (bank) => {
    // Stored as a string, which is what the native select this replaced produced.
    onChange(String(bank.org_question_bank_id));
    setAnchorEl(null);
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
      {selected ? (
        <>
          <LinkIcon sx={{ fontSize: 16, flexShrink: 0, color: 'text.disabled' }} />
          <Box
            onClick={(event) => setAnchorEl(event.currentTarget)}
            title="Change survey link"
            sx={{
              flex: 1,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              cursor: 'pointer',
              color: 'text.primary',
            }}
          >
            {selected.name}
          </Box>
          {meta ? (
            <Box component="span" sx={{ flexShrink: 0, color: 'text.disabled' }}>
              {meta}
            </Box>
          ) : null}
          <RowRemoveButton label="Remove survey link" onClick={() => onChange('')} />
        </>
      ) : (
        <Button
          variant="text"
          size="small"
          disabled={!banks.length}
          onClick={(event) => setAnchorEl(event.currentTarget)}
          sx={denseRowButtonSx}
        >
          <LinkIcon sx={rowIconSx} />
          {banks.length ? addLabel : 'No active question banks'}
        </Button>
      )}

      <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={() => setAnchorEl(null)}>
        {banks.map((bank) => (
          <MenuItem
            key={bank?.org_question_bank_id}
            selected={bank === selected}
            onClick={() => choose(bank)}
          >
            {bank?.name}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

/**
 * `dense` is the composer's footer strip: a quiet text button that inherits the strip's caption
 * type, rather than the standalone contained button this is everywhere else. Same control, same
 * file input, so nothing about picking a file changes with it.
 */
export const UploadFileButton = ({
  selectedFile,
  setSelectedFile,
  handleFileSelect,
  label = 'Upload Media',
  dense = false,
}) => (
  <>
    {!selectedFile ? (
      <Button
        component="label"
        role={undefined}
        variant={dense ? 'text' : 'contained'}
        size={dense ? 'small' : 'medium'}
        tabIndex={-1}
        sx={
          dense
            ? denseRowButtonSx
            : {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 'fit-content',
              }
        }
      >
        <CloudUploadIcon sx={dense ? rowIconSx : { marginRight: 1 }} />
        {label}
        <Input
          type="file"
          sx={{
            clip: 'rect(0 0 0 0)',
            clipPath: 'inset(50%)',
            height: 1,
            overflow: 'hidden',
            position: 'absolute',
            bottom: 0,
            left: 0,
            whiteSpace: 'nowrap',
            width: 1,
          }}
          onChange={handleFileSelect}
          value=""
        />
      </Button>
    ) : dense ? (
      <AttachmentRow file={selectedFile} onRemove={() => setSelectedFile(null)} />
    ) : (
      <Typography>
        Attachment:
        <Chip
          label={selectedFile.name}
          size="small"
          color="info"
          variant="outlined"
          onDelete={() => setSelectedFile(null)}
          sx={{ marginLeft: 1 }}
        />
      </Typography>
    )}
  </>
);

// Toggles the texting task between voter-targeted and uploaded-numbers (manual batch)
// modes in place — no navigation. Label reflects the mode you can switch to.
export const UploadManualNumbersButton = ({ isUploadMode, onToggle }) => {
  const alert = useAlert();

  return (
    <Button
      variant="contained"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 'fit-content',
      }}
      onClick={() => {
        alert
          .confirm({
            severity: 'success',
            title: isUploadMode ? 'Switch to selecting voters?' : 'Use own list?',
            description: isUploadMode
              ? 'Switching to voter selection will discard the uploaded phone numbers.'
              : 'Uploading a manual list of phone numbers may result in your text messages not being connected to individual voter profile activity.',
            confirmationText: isUploadMode ? 'Select voters' : 'Use my own list',
          })
          .then((confirm) => {
            if (confirm) {
              onToggle();
            }
          });
      }}
    >
      {isUploadMode ? 'Select voters' : 'Use my own list'}
    </Button>
  );
};

export const RecordAudioButton = ({ onRecordingComplete, label = 'Record' }) => {
  const { startRecording, stopRecording, isRecording, recordingBlob } = useAudioRecorder();

  useEffect(() => {
    if (recordingBlob && onRecordingComplete) {
      onRecordingComplete(recordingBlob);
    }
  }, [recordingBlob]);

  return (
    <Button
      color={isRecording ? 'error' : 'info'}
      component="label"
      role={undefined}
      variant="contained"
      tabIndex={-1}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 'fit-content',
      }}
      onClick={() => {
        if (isRecording) {
          stopRecording();
        } else {
          startRecording();
        }
      }}
    >
      <MicTwoTone
        sx={{
          marginRight: 1,
        }}
      />
      {isRecording ? 'Stop Recording' : label}
    </Button>
  );
};
